/** @fileoverview Warns before the signed-in Platform access token expires. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  getPlatformSessionExpiresAt,
  logout,
  refreshPlatformSession,
} from '../services/platformAuth';

const WARNING_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

export default function SessionExpiryDialog() {
  const [expiresAt, setExpiresAt] = useState(getPlatformSessionExpiresAt);
  const [now, setNow] = useState(Date.now());
  const [open, setOpen] = useState(false);
  const [refreshingSession, setRefreshingSession] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const refreshInFlight = useRef(false);
  const logoutStarted = useRef(false);

  const handleLogout = useCallback(() => {
    if (logoutStarted.current) return;
    logoutStarted.current = true;
    void logout().catch((error: unknown) => console.error('Failed to log out:', error));
  }, []);

  const syncExpiry = useCallback(() => {
    const currentExpiry = getPlatformSessionExpiresAt();
    if (currentExpiry > 0 && currentExpiry !== expiresAt) {
      setExpiresAt(currentExpiry);
      setNow(Date.now());
      setOpen(false);
    }
  }, [expiresAt]);

  const handleContinue = useCallback(async () => {
    setRefreshingSession(true);
    setRefreshError('');
    refreshInFlight.current = true;
    try {
      await refreshPlatformSession();
      const currentExpiry = getPlatformSessionExpiresAt();
      if (!currentExpiry || currentExpiry <= Date.now()) {
        throw new Error('The refreshed session has no future expiry.');
      }
      setExpiresAt(currentExpiry);
      setNow(Date.now());
      setOpen(false);
    } catch {
      setRefreshError('Unable to continue your session. Please try again.');
    } finally {
      refreshInFlight.current = false;
      setRefreshingSession(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('platform-session-refreshed', syncExpiry);
    return () => window.removeEventListener('platform-session-refreshed', syncExpiry);
  }, [syncExpiry]);

  useEffect(() => {
    if (!expiresAt) return undefined;
    const warningDelay = expiresAt - Date.now() - WARNING_BEFORE_EXPIRY_MS;
    const expiryDelay = expiresAt - Date.now();
    if (warningDelay <= 0 && expiryDelay > 0) setOpen(true);
    const warningTimer =
      warningDelay > 0 ? window.setTimeout(() => setOpen(true), warningDelay) : undefined;
    const expiryTimer = window.setTimeout(
      () => {
        if (!refreshInFlight.current) handleLogout();
      },
      Math.max(0, expiryDelay)
    );
    return () => {
      if (warningTimer) window.clearTimeout(warningTimer);
      window.clearTimeout(expiryTimer);
    };
  }, [expiresAt, handleLogout, refreshingSession]);

  useEffect(() => {
    if (!open) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [open]);

  const secondsLeft = Math.max(0, Math.ceil((expiresAt - now) / 1000));
  const timeLeft = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !refreshingSession) void handleContinue();
      }}
    >
      <DialogContent className="gap-4 p-5 sm:max-w-xl" showCloseButton={false}>
        <DialogHeader className="gap-3">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="text-xl font-bold">Session ending soon</DialogTitle>
            <span
              className="shrink-0 text-sm text-muted-foreground tabular-nums"
              aria-label={`Session expires in ${timeLeft}`}
            >
              {timeLeft} remaining
            </span>
          </div>
          <DialogDescription className="text-base leading-relaxed">
            Select “Continue Working” to renew your session and stay signed in. If it expires,
            you’ll need to sign in again.
          </DialogDescription>
        </DialogHeader>
        {refreshError && (
          <p className="text-base text-destructive" role="alert">
            {refreshError}
          </p>
        )}
        <DialogFooter className="border-0 bg-transparent p-0">
          <Button
            type="button"
            variant="outline"
            className="text-base"
            disabled={refreshingSession}
            onClick={handleLogout}
          >
            Logout
          </Button>
          <Button
            type="button"
            className="border-app-action bg-app-action text-base text-app-action-foreground hover:bg-app-action-hover"
            disabled={refreshingSession}
            onClick={() => void handleContinue()}
          >
            {refreshingSession ? 'Continuing…' : 'Continue Working'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
